import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  Avatar,
  Alert,
  Divider,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '../Loading/LoadingButton';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { personsAPI } from '../../services/api';
import { toast } from 'react-toastify';

export interface PersonEditDialogProps {
  open: boolean;
  onClose: () => void;
  person?: any;
  onSave?: () => void;
  showAdvancedFields?: boolean;
}

const PersonEditDialog: React.FC<PersonEditDialogProps> = ({
  open,
  onClose,
  person,
  onSave,
  showAdvancedFields = false,
}) => {
  const [form, setForm] = useState<any>({
    name: '',
    email: '',
    types: [],
    phone: '',
    address: '',
    nationality: '',
    notes: '',
    dateOfBirth: '',
    placeOfBirth: '',
    gender: '',
    maritalStatus: '',
    occupation: '',
    currentResidence: '',
    taxResidency: '',
    taxIdentificationNumber: '',
    passportNumber: '',
    passportExpiryDate: '',
  });

  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (person) {
      setForm({
        name: person.name || '',
        email: person.email || '',
        types: Array.isArray(person.types) ? person.types : [],
        phone: person.phone || '',
        address: person.address || '',
        nationality: person.nationality || '',
        notes: person.notes || '',
        dateOfBirth: person.dateOfBirth ? new Date(person.dateOfBirth).toISOString().split('T')[0] : '',
        placeOfBirth: person.placeOfBirth || '',
        gender: person.gender || '',
        maritalStatus: person.maritalStatus || '',
        occupation: person.occupation || '',
        currentResidence: person.currentResidence || '',
        taxResidency: person.taxResidency || '',
        taxIdentificationNumber: person.taxIdentificationNumber || '',
        passportNumber: person.passportNumber || '',
        passportExpiryDate: person.passportExpiryDate ? new Date(person.passportExpiryDate).toISOString().split('T')[0] : '',
      });
      setSelectedPhoto(null);
      setPhotoPreview(null);
    } else {
      // Reset for new person
      setForm({
        name: '',
        email: '',
        types: [],
        phone: '',
        address: '',
        nationality: '',
        notes: '',
        dateOfBirth: '',
        placeOfBirth: '',
        gender: '',
        maritalStatus: '',
        occupation: '',
        currentResidence: '',
        taxResidency: '',
        taxIdentificationNumber: '',
        passportNumber: '',
        passportExpiryDate: '',
      });
      setSelectedPhoto(null);
      setPhotoPreview(null);
    }
  }, [person, open]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setSelectedPhoto(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeletePhoto = async () => {
    if (!person?.id) return;
    if (!window.confirm('Delete profile photo?')) return;

    try {
      await personsAPI.deleteProfilePhoto(String(person.id));
      toast.success('Profile photo deleted');

      // Reset photo state
      setSelectedPhoto(null);
      setPhotoPreview(null);

      if (onSave) onSave();
    } catch (e: any) {
      toast.error('Failed to delete profile photo');
      console.error(e);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = { ...form };

      if (person?.id) {
        // Update existing person
        await personsAPI.update(String(person.id), payload);

        // Upload profile photo if selected
        if (selectedPhoto) {
          await personsAPI.uploadProfilePhoto(String(person.id), selectedPhoto);
        }

        toast.success('Person updated');
      } else {
        // Create new person
        const response = await personsAPI.create(payload);
        const newPersonId = response.data?.id;

        // Upload profile photo if selected
        if (selectedPhoto && newPersonId) {
          await personsAPI.uploadProfilePhoto(String(newPersonId), selectedPhoto);
        }

        toast.success('Person created');
      }

      // Reset state
      setSelectedPhoto(null);
      setPhotoPreview(null);

      if (onSave) onSave();
      onClose();
    } catch (e: any) {
      toast.error(person?.id ? 'Failed to update person' : 'Failed to create person');
      console.error(e);
    }
    finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!person?.id) return;
    if (!window.confirm('Delete this person? This action cannot be undone.')) return;

    try {
      await personsAPI.remove(String(person.id));
      toast.success('Person deleted');

      if (onSave) onSave();
      onClose();
    } catch (e: any) {
      toast.error('Failed to delete person');
      console.error(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>
        {person?.id ? 'Edit Person' : 'Add Person'}
      </DialogTitle>
      <DialogContent sx={isMobile ? { px: 2 } : undefined}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <TextField
            label="Name *"
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            fullWidth
            autoFocus={!person?.id}
          />

          {/* Profile Photo Upload */}
          {showAdvancedFields && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Profile Photo
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={photoPreview || (person?.profilePhotoUrl && person?.id ? personsAPI.getProfilePhotoUrl(person.id) : undefined)}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                  }}
                >
                  {!photoPreview && !person?.profilePhotoUrl && getInitials(form.name || 'P')}
                </Avatar>
                <Box display="flex" gap={1} flexDirection="column">
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                  >
                    Upload Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoSelect}
                    />
                  </Button>
                  {person?.profilePhotoUrl && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleDeletePhoto}
                    >
                      Delete Photo
                    </Button>
                  )}
                </Box>
              </Box>
              {selectedPhoto && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  New photo selected: {selectedPhoto.name}
                </Alert>
              )}
            </Box>
          )}

          <TextField
            label="Email"
            type="email"
            value={form.email || ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
          />

          <TextField
            label="Phone"
            value={form.phone || ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
          />

          <TextField
            label="Nationality"
            value={form.nationality || ''}
            onChange={(e) => setForm({ ...form, nationality: e.target.value })}
            fullWidth
          />

          <TextField
            label="Address"
            value={form.address || ''}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />

          <TextField
            label="Types (comma separated)"
            value={Array.isArray(form.types) ? form.types.join(', ') : ''}
            onChange={(e) =>
              setForm({
                ...form,
                types: e.target.value
                  .split(',')
                  .map((s: string) => s.trim().toUpperCase())
                  .filter(Boolean),
              })
            }
            helperText="e.g., DIRECTOR, SHAREHOLDER, BENEFICIARY"
            fullWidth
          />

          {showAdvancedFields && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Personal Information
              </Typography>

              <TextField
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth || ''}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Place of Birth"
                value={form.placeOfBirth || ''}
                onChange={(e) => setForm({ ...form, placeOfBirth: e.target.value })}
                fullWidth
              />

              <Select
                value={form.gender || ''}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Select Gender</em>
                </MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
                <MenuItem value="PREFER_NOT_TO_SAY">Prefer not to say</MenuItem>
              </Select>

              <Select
                value={form.maritalStatus || ''}
                onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Select Marital Status</em>
                </MenuItem>
                <MenuItem value="SINGLE">Single</MenuItem>
                <MenuItem value="MARRIED">Married</MenuItem>
                <MenuItem value="DIVORCED">Divorced</MenuItem>
                <MenuItem value="WIDOWED">Widowed</MenuItem>
                <MenuItem value="SEPARATED">Separated</MenuItem>
              </Select>

              <TextField
                label="Occupation"
                value={form.occupation || ''}
                onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                fullWidth
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Residence & Tax
              </Typography>

              <TextField
                label="Current Residence (Country)"
                value={form.currentResidence || ''}
                onChange={(e) => setForm({ ...form, currentResidence: e.target.value })}
                fullWidth
              />

              <TextField
                label="Tax Residency (Country)"
                value={form.taxResidency || ''}
                onChange={(e) => setForm({ ...form, taxResidency: e.target.value })}
                fullWidth
              />

              <TextField
                label="Tax Identification Number"
                value={form.taxIdentificationNumber || ''}
                onChange={(e) => setForm({ ...form, taxIdentificationNumber: e.target.value })}
                fullWidth
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Identification
              </Typography>

              <TextField
                label="Passport Number"
                value={form.passportNumber || ''}
                onChange={(e) => setForm({ ...form, passportNumber: e.target.value })}
                fullWidth
              />

              <TextField
                label="Passport Expiry Date"
                type="date"
                value={form.passportExpiryDate || ''}
                onChange={(e) => setForm({ ...form, passportExpiryDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <Divider sx={{ my: 1 }} />
            </>
          )}

          <TextField
            label="Notes"
            value={form.notes || ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          ...(isMobile && {
            flexDirection: 'column',
            gap: 1,
            px: 2,
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
            '& > .MuiButton-root, & > .MuiLoadingButton-root': {
              width: '100%',
              minHeight: 44,
            },
          }),
        }}
      >
        {person?.id && (
          <Button color="error" onClick={handleDelete} sx={isMobile ? undefined : { mr: 'auto' }}>
            Delete
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <LoadingButton loading={saving} variant="contained" onClick={handleSave} disabled={!form.name}>
          {person?.id ? 'Save' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PersonEditDialog;
